<template>
  <div class="rc-table">
    <rc-table-item-delete
      v-if="selection.length && showDeleteModel"
      ref="delete"
      v-bind="$attrs.delete"
      :page="pageDef"
      :selection="selection"
      :type="currentTypeData"
      v-on="$listeners"
      @confirm="deleteConfirm"
    ></rc-table-item-delete>
    <rc-base-table
      ref="table"
      :skeleton="load[loading]"
      v-bind="$attrs"
      :data="comTableData"
      :source="comSource"
      :height="tableHeight"
      :highlight-current-row="highlightCurrentRow"
      :show-overflow-tooltip="showOverflowTooltip"
      v-on="$listeners"
      @selection-change="selectionChange"
      @current-change="currentChange"
      @row-click="rowClick"
      @select-all="selectAll"
      @select="select"
    >
      <template v-for="(_index, name) in $scopedSlots" :slot="name" slot-scope="scope">
        <slot :name="name" v-bind="scope" :index="_index"></slot>
      </template>
      <template slot-scope="scope">
        <component
          :is='`rc-${scope.column.type}-type`'
          v-if="columnType(scope, 'type') && !defaultType(scope) && !['radio', 'selection'].includes(scope.column.type)"
          :ref="`rc-${scope.column.type}-type`"
          v-bind="{  column: scope.column, row: scope.row, refs: $refs, self: $parent, data: comTableData }"
          v-on="$listeners"
        />
        <el-radio
          v-if="columnType(scope, 'type') && !defaultType(scope) && scope.column.type === 'radio'"
          slot="radio"
          v-model="radio"
          class="rc-table-item-radio"
          :label="radioLabel(scope.row)"
        />
        <slot v-else-if="columnType(scope, 'customer')" :name="scope.column.prop" v-bind="scope"></slot>
        <template v-if="defaultType(scope) && !columnType(scope, 'customer')">{{scope.row[scope.column.prop]}}</template>
      </template>
    </rc-base-table>
    <rc-pagination
      v-if="!noPage"
      ref="page"
      :page-info="pageDef"
      v-bind="$attrs.page"
      v-on="$listeners"
      @size-change="(data) => handleChange(data, 'size')"
      @current-change="(data) => handleChange(data, 'num')"
    />
  </div>
</template>

<script>
import {
  strFirstWith,
  getApi,
  replaceFields,
  createField,
  hasOwnProperty,
  emitFun,
  filterEmpty,
  isPlainObject,
  registerComponents,
  indexOf,
  filterColumns
} from "@/utils";
import RcPagination from "../RcPagination";
import RcBaseTable from "../RcBaseTable";
import RcTableItemDelete from "../RcTableItemDelete";

export default {
  name: "RcTable",
  components: {
    RcBaseTable,
    RcPagination,
    RcTableItemDelete,
    ...registerComponents({ fileList: require.context("../RcTableType", true, /index\.js$/) })
  },
  props: {
    replaceFields: {
      type: Object,
      default: () => ({
        pageNum: "pageNum",
        pageSize: "pageSize"
      })
    },
    page: {
      type: Object,
      default: () => ({})
    },
    noPage: {
      type: Boolean,
      default: false
    },
    showOverflowTooltip: {
      type: Boolean,
      default: true
    },
    isCreated: {
      type: Boolean,
      default: false
    },
    isMounted: {
      type: Boolean,
      default: true
    },
    api: {
      type: Object,
      default: () => ({
        model: "",
        name: ""
      })
    },
    params: {
      type: Object,
      default: () => ({})
    },
    customTable: Function,
    recordName: {
      type: String,
      default: "records"
    },
    uniqueId: {
      type: Array,
      default: () => []
    },
    tableList: Function,
    apiTip: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: String,
      default: "loading"
    },
    rowKeyToStr: {
      type: Boolean,
      default: true
    },
    rowKeysCall: Function,
    deleteApi: {
      type: Object,
      default: () => ({
        model: "",
        name: ""
      })
    },
    deleteParams: {
      type: Object,
      default: () => ({})
    },
    deleteDialog: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    },
    formParams: Function,
    clearEmptyFields: {
      type: Boolean,
      default: true
    },
    isDeleteTotal: {
      type: Boolean,
      default: true
    },
    paramsStructure: Function,
    // deleteParamsStructure: Function,
    dataList: Function,
    tableDataSourceList: Function,
    autoHeight: {
      type: Boolean,
      default: true
    },
    autoHeightResize: {
      type: Boolean,
      default: true
    },
    autoPageSize: {
      type: Boolean,
      default: false
    },
    tdHeight: {
      type: Number,
      default: 40
    },
    needClick: {
      type: Boolean,
      default: true
    },
    showDeleteModel: {
      type: Boolean,
      default: true
    }
    // rowKey: {
    //   type: String,
    //   default: 'id'
    // }
  },
  data() {
    return {
      tableData: [],
      tableDataSource: {},
      selection: [],
      pageDef: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      load: {},
      tableHeight: null,
      highlightCurrentRow: false,
      radio: "",
      currentTypeData: ""
    };
  },
  computed: {
    comColumns() {
      return this.$attrs && this.$attrs.columns;
    },
    isApi() {
      return this.isApiFn("api");
    },
    comTableData() {
      const data = this.isApi ? this.tableData : this.$attrs.data;
      this.dataList && this.dataList(data);
      return data;
    },
    comSource() {
      const { tableDataSource } = this;
      this.tableDataSourceList && this.tableDataSourceList(tableDataSource);
      return tableDataSource;
    },
    refs() {
      return {
        ...this.$refs.table.refs,
        delete: this.$refs.delete,
        rcTable: this.$refs.table,
        page: this.$refs.page
      };
    },
    table() {
      return this.refs.table;
    },
    rcTable() {
      return this.refs.rcTable;
    },
    defaultType() {
      return scope => this.columnType(scope, "type") === "default";
    },
    columnType() {
      return (scope, name) => scope.column[name];
    },
    columnsType() {
      const types = filterColumns({ data: this.$attrs.columns || [], type: "type", isFilter: true });
      const typesVal = types.map(item => item.type);
      const isRadio = this.filterType(typesVal, "radio");
      const isSelection = this.filterType(typesVal, "selection");
      return {
        types,
        typesVal,
        isRadio,
        isSelection
      };
    },
    typeRadio() {
      return this.columnsType.isRadio;
    },
    typeSelection() {
      return this.columnsType.isSelection;
    }
  },
  watch: {
    page: {
      handler(val) {
        this.setPageDef(val);
      },
      immediate: true,
      deep: true
    },
    autoHeight: {
      handler(val) {
        this.initHeight();
      },
      immediate: true
    },
    columnsType: {
      deep: true,
      immediate: true,
      handler(val) {
        this.currentType(val);
      }
    }
  },
  created() {
    this.initApi("created");
  },
  mounted() {
    this.initMounted();
  },
  methods: {
    async initMounted() {
      await this.customPageSize();
      this.initApi("mounted");
    },
    strFirstWith,
    isApiFn(api) {
      const { model, name } = this[api];
      return model && name && model[name];
    },
    createId(data) {
      return data.map(item => {
        item.id = hasOwnProperty(item, "id")
          ? item.id && String(item.id)
          : createField({ obj: item, arr: this.uniqueId });
        return item;
      });
    },
    handleChange(data, type) {
      const handle = type === "num" ? "Current" : strFirstWith(type);
      this.getTable();
      this.$emit(`handle${handle}Change`, data);
    },
    setPageDef(val) {
      this.$set(this, "pageDef", { ...this.pageDef, ...val });
      this.$emit("current-page", this.pageDef);
    },
    initParams(val) {
      const { formParams, clearEmptyFields } = this;
      const form = val ? formParams ? formParams(val.data) : val.data : {};
      const { pageDef, params } = this;
      const paramsData = replaceFields({ ...pageDef, ...form, ...params }, this.replaceFields);
      const result = clearEmptyFields ? filterEmpty(paramsData) : paramsData;
      if (this.isDeleteTotal && hasOwnProperty(result, "total")) {
        delete result["total"];
      }
      this.$emit("params", result);
      return result;
    },
    customStructure(params, name) {
      const { paramsStructure, comTableData } = this;
      const keys = this.selectionKeys(comTableData);
      return paramsStructure ? paramsStructure(params, name, { data: comTableData, keys }) : params;
    },
    initTotal(total) {
      const newTotal = typeof total === "string" ? Number(total) : total;
      this.$set(this.page, "total", newTotal);
    },
    initData(params) {
      getApi({
        self: this,
        loading: this.loading,
        api: this.isApi,
        name: "table",
        params: this.customStructure(this.initParams(params), "table"),
        call: this.initTable
      });
    },
    initTable(data, res) {
      if (data) {
        const { total } = data;
        const uniqueTh = this.uniqueId.length;
        const table = this.customTable ? this.customTable(data, res) : this.recordName ? data[this.recordName] : isPlainObject(data) ? [data] : data;
        this.initTotal(total);
        const currentTable = uniqueTh > 0 ? this.createId(table) : table;
        this.withData({ table, currentTable, data, res });
      } else {
        this.$emit("table", data, res);
      }
    },
    withData({ table, data, res }) {
      const newData = this.tableList ? this.tableList({ table, data }) : { table, data };
      this.tableData = newData.table;
      this.tableDataSource = data;
      const records = this.recordName || "sourceRecords";
      const list = { data, res, [records]: this.tableData };
      this.$emit("table", list, this.tableData);
    },
    getTable(params) {
      this.isApi && this.initData(params);
    },
    refresh(params) {
      this.getTable(params);
    },
    initApi(type) {
      this[`is${strFirstWith(type)}`] && this.isApi && this.getTable();
    },
    selectionChange(selection) {
      this.selection = selection;
    },
    reset(data, index) {
      const str = this.currentTypeData.isRadio ? "radio" : "selection";
      (this[`clear${strFirstWith(str)}`] || this[`clear${strFirstWith(str)}`])(data, index);
    },
    clearSelection() {
      this.table.clearSelection();
    },
    setVisible(state = true) {
      this.$emit("update:visible", state);
    },
    deleteConfirm(item, index, obj) {
      emitFun({ self: this, emit: false }, { item, index }, { ... obj, refs: this, name: "rcTable" });
      this.$emit("prop-source", item);
    },
    delete(data, index, obj) {
      this.setVisible();
    },
    confirm() {
      this.initDelete();
    },
    initDelete() {
      if (this.isApiFn("deleteApi")) {
        const { model, name } = this.deleteApi;
        const keys = this.selectionKeys();
        getApi({
          self: this,
          api: model[name],
          params: this.customStructure(keys, "delete") || keys,
          name: "delete",
          call: (data, res) => {
            this.reset();
            this.getTable();
            this.$emit("delete", data, res);
            this.setVisible(false);
          }
        });
      }
    },
    selectionKeys(data) {
      const rowList = data || this.selection;
      const keys = rowList.map(item => item[this.$attrs.rowKey || "id"]);
      const res = this.rowKeyToStr ? keys.join(",") : keys;
      this.rowKeysCall && this.rowKeysCall(res, keys);
      this.$emit("selection-key", res);
      return res;
    },
    getClientRect(refs, def) {
      return refs ? refs.$el.getBoundingClientRect() : def;
    },
    getHeight(tag) {
      const { table } = this.$refs;
      const dom = table.$el.querySelector(tag);
      return dom ? dom.getBoundingClientRect() : { height: this.tdHeight };
    },
    initHeight() {
      this.getCustomHeight();
      this.resizeHeight();
    },
    resizeHeight() {
      this.autoHeightResize && window.addEventListener("resize", this.getCustomHeight, false);
    },
    customPageSize() {
      const th = this.getHeight("th");
      const td = this.getHeight("td");
      if (this.autoPageSize && this.tableHeight && th && th.height && td && td.height) {
        const tableH = Math.ceil(this.tableHeight.slice(0, -2) - th.height);
        const tdH = td.height || this.tdHeight;
        const autoSize = Math.ceil(Number(tableH) / Number(tdH)) - 1;
        const { pageSize } = this.pageDef;
        this.pageDef.pageSize = autoSize > pageSize ? autoSize : pageSize;
      }
    },
    customHeight() {
      this.$nextTick(() => {
        const { table, page } = this.$refs;
        if (table) {
          const domH = document.documentElement.clientHeight || document.body.clientHeight;
          const tableObj = this.getClientRect(table, { top: 0, height: 40 });
          const { height } = this.getClientRect(page, { height: 10 });
          this.tableHeight = `${domH - tableObj.top - height}px`;
          this.customPageSize();
        }
      });
    },
    refreshHeight() {
      this.getCustomHeight();
    },
    pageCount() {
      const { comTableData } = this;
      return comTableData && comTableData.length ? comTableData.length : 10;
    },
    defHeight() {
      const count = this.pageCount();
      this.tableHeight = count * 40 + 48 + 6;
    },
    getCustomHeight() {
      const { height, maxHeight } = this.$attrs;
      const paramsHeight = height || maxHeight;
      if (paramsHeight) { this.tableHeight = paramsHeight; };
      if (this.autoHeight && !paramsHeight) { this.customHeight(); };
      if (!paramsHeight && !this.autoHeight) { this.defHeight(); }
    },
    currentType(val) {
      this.currentTypeData = val;
      this.$emit("current-type", val);
    },
    filterType(arr, val) {
      return arr.filter(item => item === val).length;
    },
    isRadio(type) {
      return type === "radio";
    },
    highlightCurrentRowState(state = true) {
      this.highlightCurrentRow = state;
    },
    setSelection(row) {
      this.setSelectionRow(row);
    },
    setSelectionRow(row) {
      if (row) {
        const current = row && row[this.$attrs.rowKey];
        const { index, arr } = indexOf({ key: current });
        this.selection = arr;
        this.setRowSelection(index === -1 ? row : "");
      }
    },
    setRowSelection(row) {
      this.table.toggleRowSelection(row);
    },
    showRadio({ type }, col) {
      return this.isRadio(type) && this.isRadio(col);
    },
    radioLabel(item) {
      return item && `____radio${item.id}`;
    },
    currentChange(row, oldRow) {
      this.typeRadio && this.setRow(row, "radio");
    },
    rowClick(row, column, event) {
      this.typeSelection && this.setRow(row, "selection");
      this.$emit("table-select", "row-click", this.selection, row, column, event);
    },
    selectAll(selection) {
      this.$emit("table-select", "select-all", selection);
    },
    select(selection, row) {
      this.$emit("table-select", "select", selection, row);
    },
    setRow(row, type) {
      if (this.needClick) {
        this.table.setCurrentRow(row);
        this.typeRadio && this.table.clearSelection();
        this.highlightCurrentRowState(Boolean(this.typeRadio));
        this.radio = this.radioLabel(row);
        this.setSelection(row);
      }
    },
    setRadio(val) {
      this.currentChange(val);
    },
    clearRadio() {
      this.setRadio(null);
    }
  }
};
</script>

<style lang="scss" scoped>
.rc-table-item-radio {
  ::v-deep  .el-radio__label {
    display: none;
  }
}
</style>
